import BackClearButton from './BackClearButton';
import { micErrorText } from '../utils/micErrors';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, parseISO, isToday, isTomorrow, isYesterday } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useT, DATE_LOCALES } from '../i18n';
import apiClient from '../api/client';
import { canShowPurchase } from '../utils/platform';
import EventCard from './EventCard';
import { useWhisper } from '../hooks/useWhisper';
import { MicIcon, StopIcon } from './icons';

/**
 * Group a flat array of events into { dateStr → [events] } ordered by date.
 * Events with no start date go into a '_nodate' bucket at the end.
 */
function groupByDate(events) {
  const map = new Map();
  events.forEach(ev => {
    const raw = ev.start;
    const dateStr = raw ? raw.slice(0, 10) : '_nodate';
    if (!map.has(dateStr)) map.set(dateStr, []);
    map.get(dateStr).push(ev);
  });
  return map;
}

export default function SearchPage() {
  const { user } = useAuth();
  const T = useT(user?.language);
  const locale = DATE_LOCALES[user?.language] || DATE_LOCALES['it'];
  const baseLang = (user?.language || 'it').split('-')[0];

  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searched, setSearched] = useState(false); // true once a real search has returned
  const [error, setError]       = useState(false);
  const debounceRef = useRef(null);
  const inputRef    = useRef(null);
  /**
   * Some screens cannot see their own keyboard.
   *
   * Measuring it (index.js, --kb) works in a browser: the visual viewport
   * shrinks and the difference is the keyboard. Inside the installed app it
   * does not — the window keeps its full height, the keyboard is painted on
   * top of it, and the page is told nothing. Three fixes have now failed on
   * that same fact, one of them costing a whole bundle.
   *
   * So this stops trying to be clever. On focus it waits for the keyboard to
   * have appeared and then LOOKS at --kb. If a number arrived, the box sits
   * above the keyboard exactly as it should. If nothing arrived, the page is
   * blind, and a blind page cannot keep anything safe at the bottom of the
   * screen — so the box moves to the top, where no keyboard can ever reach it.
   *
   * Not elegant. But "non vedo dove scrivo" is a search box you cannot use,
   * and the top of the screen is always visible.
   */
  const [kbBlind, setKbBlind] = useState(false);
  function watchKeyboard() {
    // 400ms: long enough for the keyboard animation on a slow phone.
    setTimeout(() => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--kb');
      setKbBlind(parseInt(v, 10) > 0 ? false : true);
    }, 400);
  }
  // Monotonic id of the LATEST search: a slower earlier response must never
  // overwrite the results of a newer query typed after it.
  const searchIdRef = useRef(0);

  const {
    recording: micRecording,
    transcribing: micTranscribing,
    startRecording: micStart,
    stopRecording: micStop,
    error: micError,
  } = useWhisper(user?.language || 'it');

  function handleMicToggle() {
    if (micTranscribing) return;
    if (micRecording) { micStop(); return; }
    micStart((text) => {
      const newVal = query ? query + ' ' + text : text;
      setQuery(newVal);
      doSearch(newVal);
    });
  }

  // Auto-focus the search input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Grow the search box downward-to-upward as the text wraps onto new lines
  // (capped at ~4 lines, then it scrolls internally).
  const autoSize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
    // Only show the scrollbar once we hit the cap — avoids a stray grey line.
    el.style.overflowY = el.scrollHeight > 96 ? 'auto' : 'hidden';
  };
  useEffect(() => { autoSize(inputRef.current); }, [query]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      searchIdRef.current++; // invalidate any in-flight search
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    const myId = ++searchIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const tz  = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await apiClient.get('/events/search', { params: { q: q.trim(), tz } });
      if (myId !== searchIdRef.current) return; // a newer search superseded this one
      setResults(res.data || []);
      setSearched(true);
    } catch {
      if (myId !== searchIdRef.current) return;
      setError(true);
      setResults([]);
    } finally {
      if (myId === searchIdRef.current) setLoading(false);
    }
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (!val.trim() || val.trim().length < 2) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true); // show spinner immediately for responsiveness
    debounceRef.current = setTimeout(() => doSearch(val), 500);
  }

  function handleClear() {
    setQuery('');
    setResults([]);
    setSearched(false);
    setError(false);
    inputRef.current?.focus();
  }

  // Format a YYYY-MM-DD date string into a human-readable day label
  function dayLabel(dateStr) {
    if (dateStr === '_nodate') return '—';
    try {
      const d = parseISO(dateStr);
      if (isToday(d))     return T.today.today    || 'Oggi';
      if (isTomorrow(d))  return T.today.tomorrow  || 'Domani';
      if (isYesterday(d)) return T.today.yesterday || 'Ieri';
      const year = d.getFullYear();
      const currentYear = new Date().getFullYear();
      return format(d, year !== currentYear ? 'EEEE d MMMM yyyy' : 'EEEE d MMMM', { locale });
    } catch {
      return dateStr;
    }
  }

  // A search can return years of events, and the useful one is almost always
  // "the next time this happens". So: strict chronological order, past above,
  // future below, and the nearest day still to come marked in the app's own
  // colour — a "you are here" line the eye finds instantly, with the past to
  // scroll up into and the future down.
  const sortedResults = [...results].sort((a, b) => {
    const ta = a.start ? new Date(a.start).getTime() : Infinity; // undated last
    const tb = b.start ? new Date(b.start).getTime() : Infinity;
    return ta - tb;
  });
  const grouped = groupByDate(sortedResults);
  const dayKeys = [...grouped.keys()];
  const todayKey = new Date().toLocaleDateString('en-CA');
  // Today if there's anything today, otherwise the first day still ahead.
  // Everything in the past → mark the last one, which is the closest to now.
  const datedKeys = dayKeys.filter(k => k !== '_nodate');
  const nowKey = datedKeys.find(k => k >= todayKey) || datedKeys[datedKeys.length - 1] || null;
  const nowRef = useRef(null);

  // Land on "now" instead of on the oldest event of the list.
  useEffect(() => {
    if (!nowRef.current) return;
    try { nowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
  }, [nowKey, results.length]);

  const showEmpty = searched && !loading && !error && results.length === 0 && query.trim().length >= 2;

  return (
    <>
      {/* ── Results list — padded bottom for the fixed bar ─────────────── */}
      <div style={{ paddingBottom: 150 }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0 10px' }}>
          <BackClearButton
            hasText={!!query.trim()}
            onClear={() => setQuery('')}
            label={user?.language}
          />
        </div>

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {loading && <div className="spinner" style={{ marginTop: 32 }} />}

        {/* ── Error ───────────────────────────────────────────────────── */}
        {error && !loading && (
          <div className="alert alert-error" style={{ margin: '16px 0', textAlign: 'center' }}>
            {T.today?.error || 'Could not load results. Please try again.'}
          </div>
        )}

        {/* ── No results ──────────────────────────────────────────────── */}
        {showEmpty && (
          <div className="empty-state" style={{ marginTop: 32 }}>
            <span className="empty-state-icon">🔍</span>
            <span className="empty-state-title">
              {T.search?.noResults || 'Nessun risultato per'}{' '}
              <em>"{query.trim()}"</em>
            </span>
            <span className="empty-state-sub">
              {T.search?.noResultsHint || 'Prova con parole diverse'}
            </span>
          </div>
        )}

        {/* ── Empty page: a grey example so the user knows what to do ───── */}
        {!loading && !error && !searched && query.trim().length < 2 && (
          <div className="empty-state" style={{ marginTop: 32 }}>
            <span className="empty-state-icon">🔍</span>
            <span className="empty-state-title">{T.search?.placeholder || 'Cerca eventi…'}</span>
            <span className="empty-state-sub" style={{ fontStyle: 'italic', opacity: 0.85 }}>
              {T.search?.hint}
            </span>
          </div>
        )}

        {/* ── Results grouped by date ──────────────────────────────────── */}
        {!loading && !error && results.length > 0 && (
          <>
            <p style={{ margin: '8px 0 12px', fontSize: '0.78rem', color: 'var(--color-text-muted)', paddingInlineStart: 4 }}>
              {results.length} {(() => {
                const one = { it: 'risultato', es: 'resultado', de: 'Ergebnis', fr: 'résultat', nl: 'resultaat', pt: 'resultado', ru: 'результат', pl: 'wynik', ro: 'rezultat', zh: '条结果', ja: '件', hi: 'परिणाम', ko: '개 결과', ar: 'نتيجة' };
                const many = { it: 'risultati', es: 'resultados', de: 'Ergebnisse', fr: 'résultats', nl: 'resultaten', pt: 'resultados', ru: 'результатов', pl: 'wyników', ro: 'rezultate', zh: '条结果', ja: '件', hi: 'परिणाम', ko: '개 결과', ar: 'نتائج' };
                return (results.length === 1 ? one[baseLang] : many[baseLang]) || (results.length === 1 ? 'result' : 'results');
              })()}
            </p>
            {[...grouped.entries()].map(([dateStr, evts]) => {
              const isNow = dateStr === nowKey;
              return (
              <div key={dateStr} ref={isNow ? nowRef : null}
                className={isNow ? 'day-now' : undefined}
                style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingInlineStart: 2 }}>
                  <span style={{
                    fontSize: '0.78rem', fontWeight: isNow ? 800 : 700, textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: isNow ? 'var(--color-primary-dark)' : 'var(--color-text-muted)',
                  }}>
                    {dayLabel(dateStr)}
                  </span>
                  <span style={{ flex: 1, height: 1, background: isNow ? 'var(--color-primary)' : 'var(--color-border)' }} />
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', opacity: 0.7 }}>{evts.length}</span>
                </div>
                {evts.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onDelete={(id) => setResults(prev => prev.filter(e => e.id !== id))}
                  />
                ))}
              </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── Fixed bottom bar — no frame; sage-bordered box + separate mic ── */}
      <div style={{
        position: 'fixed',
        // Above the keyboard when the page can see it; at the top of the
        // screen when it cannot. See kbBlind above.
        ...(kbBlind
          ? { top: 'env(safe-area-inset-top, 0px)', bottom: 'auto' }
          : { bottom: 'calc(var(--navh, 64px) + var(--kb, 0px) + env(safe-area-inset-bottom, 0px))' }),
        left: 0, right: 0,
        maxWidth: 480, margin: '0 auto',
        zIndex: 100,
        padding: '8px 14px',
      }}>
        {/* Search box (sage border, wraps) on the left; mic set apart on the
            right, styled exactly like the "+" (new event) page mic. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22 }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'flex-end', gap: 8,
            background: 'var(--color-card)',
            padding: '0 12px',
            borderRadius: 'var(--radius-sm)',
            border: '2px solid var(--color-primary)',
            color: 'var(--color-text)',
            minWidth: 0,
          }}>
            <textarea
              ref={inputRef}
              value={query}
              onFocus={watchKeyboard}
              onBlur={() => setKbBlind(false)}
              onChange={handleChange}
              placeholder={T.search?.placeholder || 'Cerca eventi…'}
              aria-label={T.search?.placeholder || 'Cerca eventi…'}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              rows={1}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  clearTimeout(debounceRef.current);
                  doSearch(query);
                }
              }}
              className="search-input-centered"
              style={{
                flex: 1, border: 'none', background: 'transparent',
                fontSize: '1rem', padding: '11px 0',
                color: 'var(--color-text)', outline: 'none', minWidth: 0,
                resize: 'none', overflowY: 'hidden', maxHeight: 96,
                lineHeight: 1.35, fontFamily: 'inherit',
                textAlign: query ? 'left' : 'center',
              }}
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                aria-label={T.form?.cancelButton || 'Cancel'}
                style={{
                  flexShrink: 0, background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: '1rem', alignSelf: 'center',
                  color: 'var(--color-text-muted)', padding: '4px', lineHeight: 1,
                }}
              >✕</button>
            )}
          </div>

          {/* Round mic — same look as the "+" page mic */}
          <button
            type="button"
            onClick={handleMicToggle}
            disabled={micTranscribing}
            aria-label={T.today?.microphone}
            style={{
              flexShrink: 0, width: 64, height: 64, borderRadius: '50%',
              border: 'none', color: '#fff',
              background: (micRecording || micTranscribing) ? '#c4614a' : 'var(--color-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.9rem', lineHeight: 1,
              boxShadow: '0 3px 12px rgba(78,122,82,0.45)',
              cursor: micTranscribing ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {micTranscribing ? '…' : micRecording ? <StopIcon /> : <MicIcon />}
          </button>
        </div>
        {micTranscribing && (
          <div style={{ height: 3, borderRadius: 2, background: 'var(--color-border)', overflow: 'hidden', margin: '6px 80px 0 14px' }}>
            <div style={{ height: '100%', width: '40%', borderRadius: 2, background: 'var(--color-primary)', animation: 'whisperProgress 1.2s ease-in-out infinite alternate' }} />
          </div>
        )}
        {micError === 'ai-limit' && (
          <div style={{ color: 'var(--color-primary-dark)', fontSize: '0.78rem', textAlign: 'center', padding: '6px 6px 2px' }}>
            {canShowPurchase()
              ? <a href="/settings?upgrade=1" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>{T.today?.aiLimitReached} ⭐</a>
              /* In the Play Store app: state the fact, offer no way to buy. */
              : <span>{T.today?.aiLimitReached}</span>}
          </div>
        )}
        {micError && micError !== 'ai-limit' && (
          <div role="alert" style={{ color: '#c0392b', fontSize: '0.78rem', textAlign: 'center', padding: '6px 6px 2px', lineHeight: 1.4 }}>
            {micErrorText(micError, user?.language)}
          </div>
        )}
      </div>
    </>
  );
}
